import { useContext, useEffect, useRef, useState } from 'react'
import { NVButton } from '@nv/basic-components'
import { AuthCodeInput, OnboardProgressBar } from '@nv/components'
import { LoginFlowContext, useAdminUserContext, useVoterUserContext } from '@nv/contexts'
import { VoterOnboardingContext } from '@nv/contexts'
import { CheckAnimationIcon } from '@nv/images/design/check-animation-icon'
import { XCircleAnimatedIcon } from '@nv/images/design/x-circle-animated-icon'
import { AdminVoterAuthService } from '@nv/services'
import { OnlineVoterAuthService } from '@nv/services'
import { VoterAuthService } from '@nv/services'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import styles from './AuthVerify.module.scss'
import '@djthoms/pretty-checkbox'

export const AuthVerify = () => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language

  const navigate = useNavigate()
  const LOGIN_CONTEXT = useContext(LoginFlowContext)
  const location = useLocation()
  const { authMethod, setAuthMethod } = useContext(VoterOnboardingContext)
  const VoterUser = useVoterUserContext().voterUser
  const { setVoterUser } = useVoterUserContext()
  const { setAdminUser } = useAdminUserContext()
  const AdminUser = useAdminUserContext().adminUser
  const [code, setCode] = useState('')
  const [notValid, setNotValid] = useState(true)
  const [codeError, setCodeError] = useState(false)
  const [isResetCodeClicked, setIsResetCodeClicked] = useState(false)
  const [isResetCodeSuccess, setIsResetCodeSuccess] = useState(null)
  const [numOfAttempts, setNumOfAttempts] = useState(0)
  const authInputRef = useRef(null)
  const isLoginFlow = LOGIN_CONTEXT.loginFlow !== 'Onboarding'
  const factorSid = location?.state?.factorSid

  const verifyVoterOnbaording = async () => {
    let resp
    if (authMethod === 'SMS') resp = await OnlineVoterAuthService.verifySMSCode(code)
    else if (authMethod === 'Email') resp = await OnlineVoterAuthService.verifyEmailCode(code)
    else resp = await OnlineVoterAuthService.verifyAuthAppQRCode(code)

    if (resp.success) {
      if (resp.user) setVoterUser(resp.user)
      navigate('/voting/voter-auth/create-password')
    } else if (resp.error) setCodeError(true)
  }

  const verifyAdminLogin = async () => {
    const resp = await AdminVoterAuthService.verifyAuthAppCode(code)
    if (resp.error) setCodeError(true)

    // else automatic redirect
  }

  const verifyAdminOnboarding = async () => {
    // 2nd step verify sms & setup auth app
    if (authMethod === 'SMS') {
      const resp = await AdminVoterAuthService.verifySMSCodeOnboard(code)
      if (resp.user) setAdminUser(resp.user)
      if (resp.error) setCodeError(true)
      if (resp.success) {
        navigate('/admin/auth/auth-app-setup', {
          state: {
            qrCodeDataUrl: resp.qrCodeDataUrl,
            factorSid: resp.factorSid,
          },
        })
      }
    }
    // 3rd step verify auth app
    else {
      const resp = await AdminVoterAuthService.verifyAuthAppCodeOnboard(code, factorSid)
      if (resp.user) {
        setAdminUser(resp.user)
      }
      if (resp.error) {
        setCodeError(true)
      }
      if (resp.passwordResetRequired) {
        navigate('/admin/auth/change-password', { state: { flow: 'Onboarding' } })
      }
    }
  }

  const verifyAdminForgotPassword = async () => {
    const resp = await AdminVoterAuthService.verifyAuthAppCodePasswordResetFlow(code)
    if (resp.success) {
      if (resp.user) setAdminUser(resp.user)
      navigate('/admin/auth/change-password', { state: { flow: 'Forgot Password' } })
    }
    if (resp.error) setCodeError(true)
  }

  const verifyVoterForgotPassword = async () => {
    let resp
    if (authMethod === 'SMS') resp = await VoterAuthService.verifySMSCodePasswordResetFlow(code)
    else if (authMethod === 'Email') resp = await VoterAuthService.verifyEmailCodePasswordResetFlow(code)
    else resp = await VoterAuthService.verifyAuthAppCodePasswordResetFlow(code)

    if (resp.success) {
      if (resp.user) setVoterUser(resp.user)
      navigate('/voting/voter-auth/change-password', { state: { flow: 'Forgot Password' } })
    }
    if (resp.error) setCodeError(true)
  }

  const verifyVoterLogin = async () => {
    let resp
    if (authMethod === 'SMS') resp = await VoterAuthService.verifySMSCode(code)
    else if (authMethod === 'Email') resp = await VoterAuthService.verifyEmailCode(code)
    else resp = await VoterAuthService.verifyAuthAppCode(code)

    if (resp.user) setVoterUser(resp.user)
    if (resp.error) setCodeError(true)

    // else if  successful user will be logged in and redirected to dashboard
  }

  const onClickVerify = async () => {
    switch (LOGIN_CONTEXT.loginFlow) {
      case 'Onboarding':
        verifyVoterOnbaording()
        break
      case 'Admin Login':
        verifyAdminLogin()
        break
      case 'Admin Onboarding':
        verifyAdminOnboarding()
        break
      case 'Admin Forgot Password':
        verifyAdminForgotPassword()
        break
      case 'Forgot Password':
        verifyVoterForgotPassword()
        break
      default:
        verifyVoterLogin()
        break
    }
  }

  const onClickSendSMS = async () => {
    clearCodeError()
    setAuthMethod('SMS')
    setIsResetCodeClicked(true)

    try {
      let response
      switch (LOGIN_CONTEXT.loginFlow) {
        case 'Onboarding':
          response = await OnlineVoterAuthService.sendSMSCode(currentLanguage)
          break
        case 'Forgot Password':
          response = await VoterAuthService.sendSMSCodePasswordResetFlow(currentLanguage)
          break
        default:
          response = await VoterAuthService.sendSMSCode(currentLanguage)
          break
      }

      if (response.success) setIsResetCodeSuccess(true)
      else setIsResetCodeSuccess(false)
    } catch (error) {
      setIsResetCodeSuccess(false)
    }

    setTimeout(() => {
      setIsResetCodeClicked(false)
      setIsResetCodeSuccess(null)
    }, 20000)
  }

  const onClickSendEmail = async () => {
    clearCodeError()
    setAuthMethod('Email')
    setIsResetCodeClicked(true)

    try {
      let response
      switch (LOGIN_CONTEXT.loginFlow) {
        case 'Onboarding':
          response = await OnlineVoterAuthService.sendEmailCode()
          break
        case 'Forgot Password':
          response = await VoterAuthService.sendEmailCodePasswordResetFlow()
          break
        default:
          response = await VoterAuthService.sendEmailCode()
          break
      }

      if (response.success) setIsResetCodeSuccess(true)
      else setIsResetCodeSuccess(false)
    } catch (error) {
      setIsResetCodeSuccess(false)
    }

    setTimeout(() => {
      setIsResetCodeClicked(false)
      setIsResetCodeSuccess(null)
    }, 20000)
  }

  const clearCodeError = () => {
    setCodeError(false)
    setCode('')
    authInputRef.current.focus()
  }

  const onHandleNumOfAttempts = (counter) => {
    if (codeError && counter >= 2) {
      // attempt #2 - clear inputs & focus on first input
      setCode('')
      authInputRef.current.focus()
    }
  }

  const getPhoneDisplay = () => {
    const phone = VoterUser?.phone || AdminUser?.phone || ''
    const lastPhoneDigits = phone?.length > 4 ? phone?.substr(phone?.length - 4) : ''
    return lastPhoneDigits ? ` ****${lastPhoneDigits}.` : '.'
  }

  const getEmailDisplay = () => {
    let emailDisplay = ''
    const email = VoterUser?.email || AdminUser?.email || '.'
    if (email && email.length > 2 && email.includes('@')) {
      emailDisplay = ` ${email.substr(0, 2)}****${email.substr(email.indexOf('@'))}.`
    }
    return emailDisplay
  }

  useEffect(() => {
    if (codeError) {
      const counter = numOfAttempts + 1
      setNumOfAttempts(counter)
      onHandleNumOfAttempts(counter)
    }
  }, [codeError])

  useEffect(() => {
    if (code.length) setCodeError(false)
    if (code.length === 6) {
      setNotValid(false)
      onClickVerify()
    }
  }, [code])

  return (
    <div className={styles['container']}>
      <div className={clsx(styles['form'])}>
        {LOGIN_CONTEXT.loginFlow === 'Onboarding' && <OnboardProgressBar currentStep={3} />}

        <div className={clsx(styles['body'], isLoginFlow && styles['body--login'])}>
          <span className={styles['title']}>{t('auth.login.validations.loginVerification')}</span>

          <span className={styles['subtitle']}>
            {authMethod === 'SMS'
              ? `${t('auth.login.validations.enterVerificationCodeNumber')}${getPhoneDisplay()}`
              : authMethod === 'Email'
              ? `${t('auth.login.validations.enterVerificationCodeEmail')}${getEmailDisplay()}`
              : t('auth.login.validations.enterVerificationCodeApp')}
          </span>

          <AuthCodeInput authInputRef={authInputRef} code={code} setCode={setCode} length={6} codeError={codeError} />

          {!LOGIN_CONTEXT?.loginFlow?.startsWith('Admin') && (
            <span className={styles['disclaimer']}>
              {/* in case support admin users in this flow in future added both checks */}
              {(authMethod === 'SMS' && (VoterUser?.phone || AdminUser?.phone)) ||
              (authMethod === 'Email' && (VoterUser?.email || AdminUser?.email)) ? (
                <>
                  {isResetCodeClicked ? (
                    isResetCodeSuccess === null ? (
                      t('main.sending')
                    ) : isResetCodeSuccess ? (
                      <>
                        {t('events.voteVerification.sendCodeSuccess')}
                        <span className={styles['svg']}>
                          <CheckAnimationIcon />
                        </span>
                      </>
                    ) : (
                      <>
                        {t('events.voteVerification.sendCodeError')}
                        <span className={styles['svg']}>
                          <XCircleAnimatedIcon />
                        </span>
                      </>
                    )
                  ) : (
                    <>
                      {t('form.messages.didntGetIt')}
                      <NVButton
                        variant='text'
                        onClick={() => (authMethod === 'Email' ? onClickSendEmail() : onClickSendSMS())}
                        text={t('auth.login.validations.sendAgain')}
                      />
                    </>
                  )}
                </>
              ) : VoterUser?.phone || AdminUser?.phone ? (
                <>
                  {t('auth.login.validations.doNotHaveAuthApp')}
                  <NVButton variant='text' onClick={onClickSendSMS} text={t('auth.login.validations.sendSMS')} />
                </>
              ) : (
                <>
                  {t('auth.login.validations.authVerifyProblems')}
                  <NVButton variant='text' onClick={() => navigate(-1)} text={t('main.goBack')} />
                </>
              )}
            </span>
          )}
        </div>

        <div className={clsx(styles['footer'], isLoginFlow && styles['footer--login'])}>
          <NVButton
            classes={{ container: styles['footer__btn'] }}
            text={!LOGIN_CONTEXT.loginFlow?.includes('Onboarding') ? t('buttons.login') : t('buttons.next')}
            disabled={notValid}
            onClick={onClickVerify}
          />
        </div>
      </div>
    </div>
  )
}
